import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { trackPurchase } from '../services/fbPixel';
import '../styles/PayPalReturnPage.css';

const PayPalReturnPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = getAuth();
  const user = auth.currentUser;

  const [status, setStatus] = useState('processing'); // processing, success, pending, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Get payment intent from localStorage
    const paymentIntentStr = localStorage.getItem('paymentIntent');
    
    if (!paymentIntentStr) {
      setStatus('error');
      setMessage('Payment session expired. Please try again.');
      return;
    }

    const paymentIntent = JSON.parse(paymentIntentStr);
    
    // Check if payment intent is recent (within 1 hour)
    const intentAge = Date.now() - paymentIntent.timestamp;
    if (intentAge > 3600000) { // 1 hour
      setStatus('error');
      setMessage('Payment session expired. Please try again.');
      localStorage.removeItem('paymentIntent');
      return;
    }

    // Get PayPal parameters from URL
    const paymentId = searchParams.get('paymentId') || searchParams.get('token');
    const payerId = searchParams.get('PayerID');
    
    if (paymentId && payerId) {
      // Payment completed
      setPaymentDetails({
        paymentId,
        payerId,
        userId: paymentIntent.userId,
        plan: paymentIntent.plan,
        amount: paymentIntent.amount
      });

      trackPurchase({
        plan: paymentIntent.plan,
        amount: paymentIntent.amount,
        paymentId,
      });

      setStatus('pending');
      setMessage('Your payment has been received and is being processed.');
      
      // Clean up payment intent
      localStorage.removeItem('paymentIntent');
      
      // Note: The IPN handler will activate the subscription
      // For now, we just show that payment is being verified
    } else {
      // Payment was cancelled or failed
      setStatus('error');
      setMessage('Payment was cancelled or failed. Please try again.');
      localStorage.removeItem('paymentIntent');
    }
  }, [user, navigate, searchParams]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleRetry = () => {
    navigate('/payment');
  };

  const handleViewSessions = () => {
    navigate('/sessions');
  };

  return (
    <div className="paypal-return-page">
      <div className="paypal-return-container">
        {status === 'processing' && (
          <>
            <div className="status-icon">⏳</div>
            <h1>Processing Payment</h1>
            <p className="status-message">{message}</p>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="status-icon status-pending">⏳</div>
            <h1>Payment Received!</h1>
            <p className="status-message">
              Thank you for your payment! We're verifying your transaction with PayPal.
              Your subscription will be activated within a few minutes.
            </p>
            
            {paymentDetails && (
              <div className="payment-details">
                <h3>Payment Information</h3>
                <p><strong>Plan:</strong> Day Pass (24 hours)</p>
                <p><strong>Amount:</strong> ₱{paymentDetails.amount}</p>
                <p><strong>Payment ID:</strong> {paymentDetails.paymentId}</p>
                <p><strong>Status:</strong> <span className="status-pending">Verifying</span></p>
              </div>
            )}

            <p className="status-message">
              You can start using the service now. If your subscription isn't activated within 10 minutes,
              please contact support with your payment ID.
            </p>

            <div className="action-buttons">
              <button className="btn-primary" onClick={handleViewSessions}>
                View Sessions
              </button>
              <button className="btn-secondary" onClick={handleGoHome}>
                Go to Home
              </button>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="status-icon status-completed">✅</div>
            <h1>Payment Successful!</h1>
            <p className="status-message">
              Your Day Pass subscription has been activated! You now have 24 hours of unlimited access.
            </p>
            
            <div className="action-buttons">
              <button className="btn-primary" onClick={handleViewSessions}>
                Start Playing
              </button>
              <button className="btn-secondary" onClick={handleGoHome}>
                Go to Home
              </button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="status-icon">❌</div>
            <h1>Payment Issue</h1>
            <p className="status-message">{message}</p>
            
            <div className="action-buttons">
              <button className="btn-primary" onClick={handleRetry}>
                Try Again
              </button>
              <button className="btn-secondary" onClick={handleGoHome}>
                Go to Home
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PayPalReturnPage;
